import { Link } from 'react-router-dom';
import { Calendar, Edit, MapPin } from 'lucide-react';
import type { Zone } from '@/store/utm-taxonomy-store';
import UtmGeometryPreview from '@/components/utm/shared/UtmGeometryPreview';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';

interface UtmZoneDetailDrawerProps {
  zone: Zone | null;
  isOpen: boolean;
  onClose: () => void;
}

const getImpactLevelClass = (level: string) => {
  if (level === 'NOTAM') return 'bg-red-600/20 text-red-500';
  if (level === 'AUTHORISATION') return 'bg-red-500/20 text-destructive';
  if (level === 'NOTIFICATION') return 'bg-yellow-500/20 text-yellow-400';
  return 'bg-green-500/20 text-green-400';
};

const UtmZoneDetailDrawer = ({ zone, isOpen, onClose }: UtmZoneDetailDrawerProps) => {
  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="right" className="w-full overflow-y-auto border-border bg-card p-0 sm:max-w-2xl">
        {!zone ? null : (
          <div className="p-6">
            <SheetHeader>
              <SheetTitle className="text-foreground">Zone Details</SheetTitle>
              <SheetDescription className="text-muted-foreground">
                Zone overview, geometry, altitude bounds, and linked impacts.
              </SheetDescription>
            </SheetHeader>

            <div className="mt-6 space-y-6">
              <div className="space-y-4">
                <div>
                  <label className="text-xs font-medium uppercase text-muted-foreground">Zone Code</label>
                  <p className="mt-1 font-mono text-lg text-primary">{zone.zoneCode}</p>
                </div>

                <div>
                  <label className="text-xs font-medium uppercase text-muted-foreground">Zone Name</label>
                  <p className="mt-1 text-lg text-foreground">{zone.zoneName}</p>
                </div>

                {zone.description && (
                  <div>
                    <label className="text-xs font-medium uppercase text-muted-foreground">Description</label>
                    <p className="mt-1 text-sm text-foreground">{zone.description}</p>
                  </div>
                )}

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="text-xs font-medium uppercase text-muted-foreground">Status</label>
                    <p className="mt-1">
                      <span
                        className={`rounded border px-2 py-1 text-xs font-medium ${
                          zone.status === 'PUBLISHED'
                            ? 'border-green-500/50 bg-green-500/20 text-green-400'
                            : 'border-border bg-muted text-muted-foreground'
                        }`}
                      >
                        {zone.status}
                      </span>
                    </p>
                  </div>
                  <div>
                    <label className="text-xs font-medium uppercase text-muted-foreground">Group</label>
                    <p className="mt-1 text-sm text-foreground">{zone.group?.name || 'Unknown'}</p>
                  </div>
                </div>
              </div>

              {(zone.activeStart || zone.activeEnd) && (
                <div className="border-t border-border pt-4">
                  <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-foreground">
                    <Calendar size={16} />
                    <span>Active Time Window</span>
                  </h3>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    {zone.activeStart && (
                      <div>
                        <label className="text-xs font-medium uppercase text-muted-foreground">Start</label>
                        <p className="mt-1 text-sm text-foreground">
                          {new Date(zone.activeStart).toLocaleString()}
                        </p>
                      </div>
                    )}
                    {zone.activeEnd && (
                      <div>
                        <label className="text-xs font-medium uppercase text-muted-foreground">End</label>
                        <p className="mt-1 text-sm text-foreground">
                          {new Date(zone.activeEnd).toLocaleString()}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {zone.geometry && (
                <div className="border-t border-border pt-4">
                  <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-foreground">
                    <MapPin size={16} />
                    <span>Geometry & Altitude</span>
                  </h3>

                  <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <label className="text-xs font-medium uppercase text-muted-foreground">Lower Bound</label>
                      <p className="mt-1 text-sm text-foreground">
                        {zone.geometry.lowerBoundM}m {zone.geometry.lowerRef}
                      </p>
                    </div>
                    <div>
                      <label className="text-xs font-medium uppercase text-muted-foreground">Upper Bound</label>
                      <p className="mt-1 text-sm text-foreground">
                        {zone.geometry.upperBoundM}m {zone.geometry.upperRef}
                      </p>
                    </div>
                  </div>

                  <UtmGeometryPreview geometry={zone.geometry.geom} height="250px" />
                </div>
              )}

              {zone.impacts && zone.impacts.length > 0 && (
                <div className="border-t border-border pt-4">
                  <h3 className="mb-3 text-sm font-semibold text-foreground">
                    Linked Impacts ({zone.impacts.length})
                  </h3>
                  <div className="space-y-2">
                    {zone.impacts.map((impact) => (
                      <div
                        key={impact.id}
                        className="rounded-lg border border-border bg-muted p-3"
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-foreground">{impact.name}</p>
                          </div>
                          <span className={`rounded px-2 py-1 text-xs font-medium ${getImpactLevelClass(impact.level)}`}>
                            {impact.level}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex justify-end border-t border-border pt-4">
                <Link
                  to={`/admin/uzamaps/zones/${zone.id}/edit`}
                  onClick={onClose}
                  className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-primary-foreground transition-colors hover:bg-primary/90"
                >
                  <Edit size={16} />
                  <span>Edit Zone</span>
                </Link>
              </div>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default UtmZoneDetailDrawer;
